import * as THREE from "three";
import { BIRD_COATS, EAR_FLICK_EVERY, EAR_UP, RABBIT_COATS, between } from "./critterTuning";

/** A critter's body parts (critter.ts Critter owns them): built here from primitives, animated there. */
export interface CritterBody {
  readonly root: THREE.Group;
  /** Pivots on the hind feet: the hop's squash and pitch, and sitting up. */
  readonly posture: THREE.Group;
  /** Pivots at the neck: nibbling, pecking and looking round. */
  readonly head: THREE.Group;
  readonly wings: THREE.Object3D[];
  readonly ears: THREE.Object3D[];
  readonly earFlick: number[];
  readonly earFlickIn: number[];
  readonly feet: THREE.Object3D[];
  /** Each hind foot's resting z, for the kick-back in the air. */
  readonly footZ: number[];
}

/** Where the posture pivots, along the body (z): the hind feet, so sitting up rocks back onto them. */
export const HIND_PIVOT = -0.045;

function mat(color: number): THREE.MeshStandardMaterial { return new THREE.MeshStandardMaterial({ color, roughness: 0.85, metalness: 0 }); }

/** Adds `o` to the posture, placed in the critter's own space (the posture's pivot is `HindPivot` along z). */
function put(b: CritterBody, o: THREE.Object3D, x: number, y: number, z: number, parent: THREE.Object3D = b.posture): THREE.Object3D {
  o.position.set(x, y, parent === b.posture ? z - HIND_PIVOT : z);
  parent.add(o);
  return o;
}

/** A rabbit: body, head with ears and eyes, a scut, long hind feet and fore paws. */
export function buildRabbit(b: CritterBody): void {
  b.posture.position.z = HIND_PIVOT;
  const coat = mat(RABBIT_COATS[Math.floor(Math.random() * RABBIT_COATS.length)]);
  const body = new THREE.Mesh(new THREE.SphereGeometry(0.075, 10, 8), coat); body.scale.set(1, 0.85, 1.35); put(b, body, 0, 0.07, 0);
  put(b, b.head, 0, 0.1, 0.06);
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.05, 10, 8), coat); put(b, head, 0, 0.025, 0.025, b.head);
  for (const side of [-1, 1]) {
    const ear = new THREE.Mesh(new THREE.CapsuleGeometry(0.014, 0.055, 3, 6), coat);
    ear.geometry.translate(0, 0.0415, 0);                // pivots at its root, so it flicks and lays back from the skull
    put(b, ear, side * 0.022, 0.055, 0.012, b.head); ear.rotation.z = side * -0.25; ear.rotation.x = EAR_UP;
    b.ears.push(ear); b.earFlick.push(0); b.earFlickIn.push(between(EAR_FLICK_EVERY));
  }
  const eyeM = mat(0x1a1210);
  for (const side of [-1, 1]) { const e = new THREE.Mesh(new THREE.SphereGeometry(0.009, 6, 6), eyeM); put(b, e, side * 0.03, 0.035, 0.065, b.head); }
  const tail = new THREE.Mesh(new THREE.SphereGeometry(0.022, 8, 6), mat(0xd9cfbf)); put(b, tail, 0, 0.08, -0.1);   // off-white: pure white bloomed into a glowing blob
  // Feet on the ground: long hind feet (they kick out behind in the air) and two small fore paws.
  for (const side of [-1, 1]) {
    const foot = new THREE.Mesh(new THREE.SphereGeometry(0.022, 8, 6), coat); foot.scale.set(0.7, 0.45, 1.9);
    b.feet.push(put(b, foot, side * 0.04, 0.01, -0.045)); b.footZ.push(foot.position.z);
    const paw = new THREE.Mesh(new THREE.SphereGeometry(0.013, 6, 5), coat); put(b, paw, side * 0.025, 0.012, 0.075);
  }
  b.root.traverse((o) => { if ((o as THREE.Mesh).isMesh) o.castShadow = true; });
}

/** A small brown bird: body, head and beak, wings on shoulder pivots, a tail and two legs. */
export function buildBird(b: CritterBody): void {
  const coat = mat(BIRD_COATS[Math.floor(Math.random() * BIRD_COATS.length)]);
  const body = new THREE.Mesh(new THREE.SphereGeometry(0.045, 10, 8), coat); body.scale.set(1, 0.9, 1.4); put(b, body, 0, 0.058, 0);
  put(b, b.head, 0, 0.085, 0.03);
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.03, 10, 8), coat); put(b, head, 0, 0.015, 0.02, b.head);
  const beak = new THREE.Mesh(new THREE.ConeGeometry(0.01, 0.028, 6), mat(0x5a4a38)); beak.rotation.x = Math.PI / 2; put(b, beak, 0, 0.013, 0.055, b.head);
  for (const side of [-1, 1]) {
    // wing on a shoulder pivot: folded down the flank on the ground, beating from the shoulder in the air
    const shoulder = put(b, new THREE.Group(), side * 0.03, 0.072, 0);
    const w = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.006, 0.05), coat); w.position.x = side * 0.035; shoulder.add(w);
    b.wings.push(shoulder);
  }
  const tail = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.006, 0.05), coat); tail.rotation.x = 0.4; put(b, tail, 0, 0.055, -0.07);
  const legM = mat(0x4a3a2c);
  for (const side of [-1, 1]) { const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.004, 0.004, 0.03, 5), legM); b.feet.push(put(b, leg, side * 0.014, 0.015, 0)); }
  b.root.traverse((o) => { if ((o as THREE.Mesh).isMesh) o.castShadow = true; });
}
